import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ViewType from "~/@types/viewType";
import { MergeProductionSpaceCategoryInput, MergeProductionSpaceTypeInput } from "~/gql/graphqlBase.generated";
import { SpaceCapacityRowFragment } from "~/gql/queries/produce/productionSpacePlanning.generated";
import { type UnresolvedMoveOutcome } from "~produce/space-planning/helpers/commitRebalance";
import { jobLotKey, StagedMove } from "~produce/space-planning/helpers/stagedMoves";

export type PlannerDialog =
	| "capacitySource"
	| "capacityWindow"
	| "commitPlan"
	| "copyYear"
	| "duplicateWindow"
	| "movePicker";

export interface SpacePlanningInspectionRequest {
	label: string;
	target: ContinuationTarget;
}

interface SpacePlanningState {
	activeTab: string;
	capacityPanelTab: number;
	dirtyDialogs: Record<PlannerDialog, boolean>;
	inspectionRequest: null | SpacePlanningInspectionRequest;
	mergeProductionSpaceCategoryInput?: MergeProductionSpaceCategoryInput;
	mergeProductionSpaceTypeInput?: MergeProductionSpaceTypeInput;
	moveOutcomeById: Record<string, UnresolvedMoveOutcome>;
	selectedCapacityRow?: SpaceCapacityRowFragment;
	selectedLocation: string;
	selectedSite: string;
	selectedYear: string;
	spaceCategoryFormOpen: boolean;
	spaceCategoryViewType: ViewType;
	spaceTypeFormOpen: boolean;
	stagedMoves: StagedMove[];
	tableLoading: boolean;
	tableViewMode: "both" | "remaining" | "used";
	whatIfActive: boolean;
}

const initialState: SpacePlanningState = {
	activeTab: "overview",
	capacityPanelTab: 0,
	dirtyDialogs: {
		capacitySource: false,
		capacityWindow: false,
		commitPlan: false,
		copyYear: false,
		duplicateWindow: false,
		movePicker: false
	},
	inspectionRequest: null,
	moveOutcomeById: {},
	selectedLocation: "all",
	selectedSite: "all",
	selectedYear: String(new Date().getFullYear()),
	spaceCategoryFormOpen: false,
	spaceCategoryViewType: "card",
	spaceTypeFormOpen: false,
	stagedMoves: [],
	tableLoading: false,
	tableViewMode: "both",
	whatIfActive: false
};

export const spacePlanningSlice = createSlice({
	initialState,
	name: "spacePlanning",
	reducers: {
		clearInspectionRequest: (state) => {
			state.inspectionRequest = null;
		},
		clearSelectedCapacityRow: (state) => {
			state.selectedCapacityRow = undefined;
		},
		clearStagedMoves: (state) => {
			state.stagedMoves = [];
			state.moveOutcomeById = {};
		},
		closeSpaceCategoryForm: (state) => {
			state.spaceCategoryFormOpen = false;
		},
		closeSpaceTypeForm: (state) => {
			state.spaceTypeFormOpen = false;
		},
		openSpaceCategoryForm: (state) => {
			state.spaceCategoryFormOpen = true;
		},
		openSpaceTypeForm: (state) => {
			state.spaceTypeFormOpen = true;
		},
		requestInspection: (state, action: PayloadAction<SpacePlanningInspectionRequest>) => {
			state.inspectionRequest = action.payload;
		},
		setActiveTab: (state, action: PayloadAction<string>) => {
			state.activeTab = action.payload;
		},
		setCapacityPanelTab: (state, action: PayloadAction<number>) => {
			state.capacityPanelTab = action.payload;
		},
		setDialogDirty: (state, action: PayloadAction<{ dialog: PlannerDialog; isDirty: boolean }>) => {
			state.dirtyDialogs[action.payload.dialog] = action.payload.isDirty;
		},
		setMergeProductionSpaceCategoryInput: (state, action: PayloadAction<MergeProductionSpaceCategoryInput | null>) => {
			if (action.payload === null) {
				state.mergeProductionSpaceCategoryInput = undefined;
				return;
			}
			state.mergeProductionSpaceCategoryInput = { ...action.payload };
		},
		setMergeProductionSpaceTypeInput: (state, action: PayloadAction<MergeProductionSpaceTypeInput | null>) => {
			if (action.payload === null) {
				state.mergeProductionSpaceTypeInput = undefined;
				return;
			}
			state.mergeProductionSpaceTypeInput = { ...action.payload };
		},
		setMoveOutcomes: (state, action: PayloadAction<Record<string, UnresolvedMoveOutcome>>) => {
			state.moveOutcomeById = action.payload;
		},
		setSelectedCapacityRow: (state, action: PayloadAction<SpaceCapacityRowFragment | null>) => {
			if (action.payload === null) {
				state.selectedCapacityRow = undefined;
				return;
			}
			state.selectedCapacityRow = { ...action.payload };
		},
		setSelectedLocation: (state, action: PayloadAction<string>) => {
			state.selectedLocation = action.payload;
		},
		setSelectedSite: (state, action: PayloadAction<string>) => {
			state.selectedSite = action.payload;
		},
		setSelectedYear: (state, action: PayloadAction<string>) => {
			state.selectedYear = action.payload;
		},
		setSpaceCategoryViewType: (state, action: PayloadAction<ViewType>) => {
			state.spaceCategoryViewType = action.payload;
		},
		setStagedMoves: (state, action: PayloadAction<StagedMove[]>) => {
			state.stagedMoves = action.payload;
			state.moveOutcomeById = {};
		},
		setTableLoading: (state, action: PayloadAction<boolean>) => {
			state.tableLoading = action.payload;
		},
		setTableViewMode: (state, action: PayloadAction<"both" | "remaining" | "used">) => {
			state.tableViewMode = action.payload;
		},
		setWhatIfActive: (state, action: PayloadAction<boolean>) => {
			state.whatIfActive = action.payload;
		},
		stageMove: (state, action: PayloadAction<StagedMove>) => {
			const key = jobLotKey(action.payload.productionJobId, action.payload.lotId);
			state.stagedMoves = [
				...state.stagedMoves.filter((move) => jobLotKey(move.productionJobId, move.lotId) !== key),
				action.payload
			];
		},
		unstageMove: (state, action: PayloadAction<string>) => {
			state.stagedMoves = state.stagedMoves.filter((move) => move.id !== action.payload);
			delete state.moveOutcomeById[action.payload];
		},
		updateMergeProductionSpaceCategoryInput: (
			state,
			action: PayloadAction<Partial<MergeProductionSpaceCategoryInput> | null>
		) => {
			if (action.payload === null) {
				state.mergeProductionSpaceCategoryInput = undefined;
				return;
			}
			if (!state.mergeProductionSpaceCategoryInput) return;
			state.mergeProductionSpaceCategoryInput = {
				...state.mergeProductionSpaceCategoryInput,
				...action.payload
			};
		},
		updateMergeProductionSpaceTypeInput: (
			state,
			action: PayloadAction<Partial<MergeProductionSpaceTypeInput> | null>
		) => {
			if (action.payload === null) {
				state.mergeProductionSpaceTypeInput = undefined;
				return;
			}
			if (!state.mergeProductionSpaceTypeInput) return;
			state.mergeProductionSpaceTypeInput = {
				...state.mergeProductionSpaceTypeInput,
				...action.payload
			};
		}
	}
});

export const {
	clearInspectionRequest,
	clearSelectedCapacityRow,
	clearStagedMoves,
	closeSpaceCategoryForm,
	closeSpaceTypeForm,
	openSpaceCategoryForm,
	openSpaceTypeForm,
	requestInspection,
	setActiveTab,
	setCapacityPanelTab,
	setDialogDirty,
	setMergeProductionSpaceCategoryInput,
	setMergeProductionSpaceTypeInput,
	setMoveOutcomes,
	setSelectedCapacityRow,
	setSelectedLocation,
	setSelectedSite,
	setSelectedYear,
	setSpaceCategoryViewType,
	setStagedMoves,
	setTableLoading,
	setTableViewMode,
	setWhatIfActive,
	stageMove,
	unstageMove,
	updateMergeProductionSpaceCategoryInput,
	updateMergeProductionSpaceTypeInput
} = spacePlanningSlice.actions;

export default spacePlanningSlice.reducer;
